import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import SearchInput from '@/components/common/search-input';
import EmptyLayout from '@/components/layouts/empty-layout';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import type { BlacklistMember } from '@/hooks/users/use-blacklist';
import useDeleteBlacklist from '@/hooks/users/use-delete-blacklist';
import useFilteredBlacklist from '@/hooks/users/use-filtered-blacklist';
import type { PromiseLoaderReturnType } from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function BlacklistRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const { filteredBlacklist, setSearchTerm, searchTerm } = useFilteredBlacklist();
  const { mutateAsync: deleteBlacklist, isPending: isDeleting } = useDeleteBlacklist();

  const handleRemoveBlacklist = async (member: BlacklistMember) => {
    await makePrompt({
      key: 'remove-blacklist',
      title: t('blacklist.revoke-hint'),
      content: t('blacklist.revoke-confirm'),
      promise: () => deleteBlacklist(member.BlackUserID),
    });
  };

  return (
    <PageLayout
      title={t('privacy.blacklist')}
      className='gap-0'
      headerConfig={{
        navigateBack: true,
      }}
      blockOnDesktop>
      <LoadingLayout promise={loaderData?.promises}>
        {() => (
          <>
            <div className='mode-embedded:mt-4 mx-5 mb-4 flex items-center'>
              <SearchInput
                icon
                keyword={searchTerm}
                setKeyword={setSearchTerm}
                clearKeyword={searchTerm ? () => setSearchTerm('') : undefined}
                className='m-0 flex-1'
              />
            </div>

            <Virtuoso
              className='mx-5 flex-1'
              data={filteredBlacklist}
              overscan={6}
              components={{
                EmptyPlaceholder: () => <EmptyLayout label='blacklist.empty' />,
              }}
              itemContent={(_, item) => (
                <div key={item.ID}>
                  <AppCell
                    label={<p className='text-h2'>{item.BlackUserNickName}</p>}
                    className='px-0'
                    prefix={
                      <AvatarWithStatus
                        className='size-12'
                        size={[48, 48]}
                        imageUrl={item.BlackUserHeadImg}
                        label={item.BlackUserNickName}
                      />
                    }
                    suffix={
                      <button
                        type='button'
                        className='btn btn-primary btn-sm'
                        disabled={isDeleting}
                        onClick={() => handleRemoveBlacklist(item)}>
                        {t('blacklist.revoke')}
                      </button>
                    }
                  />
                </div>
              )}
            />
          </>
        )}
      </LoadingLayout>
    </PageLayout>
  );
}
